import { alpha, darken, lighten } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { Shadows } from '@mui/material/styles/shadows';

declare module '@mui/material/styles' {
    interface TypographyVariants {
        body1bold: React.CSSProperties;
        body2bold: React.CSSProperties;
        body3: React.CSSProperties;
        h5mobile: React.CSSProperties;
        helperText: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        body1bold?: React.CSSProperties;
        body2bold?: React.CSSProperties;
        body3?: React.CSSProperties;
        h5mobile?: React.CSSProperties;
        helperText?: React.CSSProperties;
    }

    interface Palette {
        black: Palette['primary'];
        white: Palette['primary'];
    }

    interface PaletteOptions {
        black: PaletteOptions['primary'];
        white: PaletteOptions['primary'];
    }
}
declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        black: true;
        white: true;
    }
}
// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        body1bold: true;
        body2bold: true;
        body3: true;
        h5mobile: true;
        helperText: true;
    }
}

/**
 * The primary Fixtuur branding theme, any tenant themes should override this where absolutely necessary
 */
export const fixtuur = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 940,
            lg: 1200,
            xl: 1536,
        },
    },
    palette: {
        primary: {
            main: '#FB3569',
            dark: '#C50B4A',
            light: '#FF6C8E',
            contrastText: '#FFFFFF',
        },
        black: {
            main: '#333745',
            contrastText: '#FFFFFF',
        },
        white: {
            main: '#FFFFFF',
            contrastText: '#000000',
        },
        secondary: {
            main: '#18181B',
            dark: '#09090A',
            light: lighten('#18181B', 0.9),
            contrastText: '#FFFFFF',
        },
        background: { paper: '#FFFFFF', default: '#FAFAFA' },
        error: {
            main: '#EC7304',
            dark: darken('#FF7A00', 0.1),
            light: lighten('#FF7A00', 0.4),
            contrastText: '#FFFFFF',
        },
        info: {
            main: '#016FB9',
            dark: darken('#016FB9', 0.2),
            light: lighten('#016FB9', 0.4),
            contrastText: '#FFFFFF',
        },
        warning: {
            main: '#EAB806',
            dark: darken('#EDBB08', 0.2),
            light: lighten('#EDBB08', 0.4),
            contrastText: 'rgba(0, 0, 0, 0.12)',
        },
        success: {
            main: '#14BB25',
            dark: darken('#14BB25', 0.4),
            light: lighten('#14BB25', 0.2),
            contrastText: '#FFFFFF',
        },
        text: {
            primary: '#18181B',
            secondary: '#70707B',
            disabled: '#E4E4E7',
        },
        action: {
            active: alpha('#000000', 0.54),
            hover: alpha('#333745', 0.07),
            selected: alpha('#333745', 0.1),
            disabled: alpha('#333745', 0.26),
            disabledBackground: alpha('#333745', 0.12),
            focus: alpha('#333745', 0.12),
        },
        divider: 'rgba(4, 4, 6, 0.12)',
    },
    typography: {
        fontFamily: 'Inter Tight',
        fontSize: 16,
        h1: { fontSize: '6rem', fontWeight: 700, lineHeight: '7rem' },
        h2: { fontSize: '3.75rem', fontWeight: 700, lineHeight: '4.5rem' },
        h3: { fontSize: '3rem', fontWeight: 700, lineHeight: '3.5rem' },
        h4: {
            fontSize: '2.125rem',
            fontWeight: 600,
            lineHeight: '2.625rem',
        },
        h5: {
            fontSize: '1.4375rem',
            fontWeight: 600,
            lineHeight: '1.9375rem',
        },
        h5mobile: {
            fontFamily: 'Inter Tight',
            fontSize: '1.rem',
            fontWeight: 600,
            lineHeight: '1.5rem',
        },
        h6: {
            fontSize: '1.125rem',
            fontWeight: 600,
            lineHeight: '1.8125rem',
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.25rem',
        },
        body1bold: {
            //TODO: find out why custom variants don't respect overall font family
            fontFamily: 'Inter Tight',
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: '1.25rem',
        },
        body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: '1.25rem',
        },
        body2bold: {
            fontFamily: 'Inter Tight',
            fontSize: '0.875rem',
            fontWeight: 700,
            lineHeight: '1.3125rem',
        },
        body3: {
            fontFamily: 'Inter Tight',
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.25rem',
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: '1.75rem',
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: '1.375rem',
        },
        caption: {
            fontSize: '0.6875rem',
            fontWeight: 600,
            lineHeight: '1.125rem',
        },
        overline: {
            fontSize: '0.75rem',
            fontWeight: 500,
            lineHeight: '2rem',
        },
        helperText: {
            fontFamily: 'Inter Tight',
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: '1.25rem',
        },
    },
    shadows: Array(25).fill('none') as Shadows,
    components: {
        MuiButton: {
            styleOverrides: {
                root: ({ ownerState, theme }) => ({
                    borderRadius: '4px',
                    textTransform: 'none',
                    ...(ownerState.size === 'small'
                        ? {
                              padding: '6px 10px',
                          }
                        : { height: '48px' }),
                    ...(ownerState.size === 'large' && { fontSize: '15px', padding: '11px 22px' }),
                    ...(ownerState.variant === 'text' && {
                        '&:hover': {
                            backgroundColor: lighten(theme.palette.primary.main, 0.9),
                            color: theme.palette.primary.main,
                        },
                    }),
                    ...(ownerState.variant === 'outlined' && {
                        '&:hover': {
                            backgroundColor: lighten(theme.palette.primary.main, 0.9),
                            color: theme.palette.primary.main,
                            borderColor: theme.palette.primary.main,
                        },
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
                    }),
                }),
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: '8px',
                    '&:hover': {
                        backgroundColor: lighten(theme.palette.primary.main, 0.9),
                        color: theme.palette.primary.main,
                    },
                }),
            },
        },
    },
});

/**
 * Constructs a new theme object with overrides for tenant specific brand colour. Defaults to fixtuur theme.
 * Should really only be used during initialisation, to access the theme at component level, use
 * instead the useTheme hook from '@mui/material'.
 * @param brandColour string hex colour for brand
 * @returns Theme
 */
export const getTheme = (brandColour?: string) => {
    if (brandColour) {
        return createTheme(fixtuur, {
            palette: {
                primary: {
                    main: brandColour,
                    dark: darken(brandColour, 0.3),
                    light: lighten(brandColour, 0.3),
                },
            },
        });
    } else {
        return fixtuur;
    }
};
